/**
 * Phase 14.5 Step 4 — New Campaign wizard host page.
 *
 * Three-step wizard backed by react-hook-form, with the actual POST
 * dispatch wired in Step 4:
 *   Step 1 — Scenario id + campaign name.
 *   Step 2 — Five dimension toggles + per-dimension form sections,
 *            all registry-driven via `useCampaignRegistries`.
 *   Step 3 — Review: dispatch lane resolved by `deriveCampaignType`,
 *            payload assembled by `assemblePayload`, validated by
 *            the zod `buildSubmitSchema(lane, registries)`. Submit
 *            posts to the right backend endpoint and navigates to
 *            `/campaigns/:id` (which re-dispatches to the family-
 *            specific detail surface).
 *
 * Two error surfaces (kept ORTHOGONAL — refinement C):
 *   • dispatch_blocking_errors[]  — toggle mutual-exclusion (Step 2)
 *   • zod_field_errors[]          — scalar / array bound violations
 * Both must be empty before submit fires.
 */
import { useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useForm, useWatch } from "react-hook-form";
import { toast } from "sonner";
import { AlertTriangle, Loader2 } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import BaselineScenarioPicker from "@/components/optimisation/BaselineScenarioPicker";
import BaselineAirframePicker from "@/components/optimisation/BaselineAirframePicker";
import WizardShell, { WIZARD_STEPS } from "@/components/campaigns/WizardShell";
import PerturbationSection from "@/components/campaigns/sections/PerturbationSection";
import SensitivitySection from "@/components/campaigns/sections/SensitivitySection";
import TargetPkSection from "@/components/campaigns/sections/TargetPkSection";
import GeometrySection from "@/components/campaigns/sections/GeometrySection";
import DegradationSection from "@/components/campaigns/sections/DegradationSection";
import deriveCampaignType, {
    assemblePayload,
    PK_DISPATCH_ENDPOINT,
    GEOMETRY_DISPATCH_ENDPOINT,
    OPTIMISATION_DISPATCH_ENDPOINT,
} from "@/lib/campaignDispatch";
import { buildSubmitSchema } from "@/lib/campaignSchemas";
import useCampaignRegistries from "@/hooks/useCampaignRegistries";
import pkCampaigns from "@/api/pkCampaigns";
import geometryCampaigns from "@/api/geometryCampaigns";
import optimisationCampaigns from "@/api/optimisationCampaigns";
import OptimisationSection, {
    OPTIMISATION_DEFAULTS,
} from "@/components/campaigns/sections/OptimisationSection";

const FORM_DEFAULTS = {
    name: "",
    scenario_id: "",
    // Phase 14.9 W2 — campaign-level airframe overrides. Empty = use
    // scenario default at backend compile time. The pickers default-
    // select the first verified airframe of each role on mount.
    interceptor_airframe_id: "",
    target_airframe_id: "",
    perturbation: { enabled: false, preset: "nominal" },
    sensitivity: { enabled: false, method: "variance_first_order", samples_per_variable: 20, variable_subset: [] },
    target_pk: { enabled: false, target_pk: 0.6, max_iterations: 30, samples_per_iteration: 20, lambda_penalty: 0.1, variable_budget: [] },
    geometry: { enabled: false, sampler: "uniform", n_points: 50, samples_per_point: 5, envelope: {} },
    degradation: { enabled: false, profile_id: "" },
    optimisation: { enabled: false, ...OPTIMISATION_DEFAULTS },
};

function clampStep(raw) {
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n)) return 1;
    return Math.min(WIZARD_STEPS.length, Math.max(1, n));
}

export default function NewCampaignWizardPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const currentStep = clampStep(searchParams.get("step"));
    const [submitting, setSubmitting] = useState(false);
    const [submitErr, setSubmitErr] = useState(null);

    const { control, setValue } = useForm({ defaultValues: FORM_DEFAULTS });
    const formValues = useWatch({ control }) || FORM_DEFAULTS;

    const { loading: registriesLoading, error: registriesError, registries } =
        useCampaignRegistries({ scenarioId: formValues.scenario_id || undefined });

    const dispatch = useMemo(
        () => deriveCampaignType({
            perturbation: formValues.perturbation?.enabled,
            sensitivity: formValues.sensitivity?.enabled,
            target_pk: formValues.target_pk?.enabled,
            geometry: formValues.geometry?.enabled,
            degradation: formValues.degradation?.enabled,
            optimisation: formValues.optimisation?.enabled,
        }),
        [formValues],
    );

    // Assemble the lane-specific payload + run zod validation.
    // Both happen on every render (cheap; pure functions) so the
    // Review step can render the error list reactively.
    const payload = useMemo(() => {
        if (!dispatch.campaign_type) return null;
        // Phase 14.5 Step 6 (Y2d) — pass pk-campaigns registry's
        // `resolved_specs` so the perturbation preset can be
        // translated into a concrete `perturbation_overrides` map.
        return assemblePayload(formValues, dispatch.campaign_type, {
            pkResolvedSpecs: registries?.pk?.resolved_specs || null,
        });
    }, [formValues, dispatch.campaign_type, registries]);

    const zodResult = useMemo(() => {
        if (!payload || !dispatch.campaign_type) return { success: true, errors: [] };
        const schema = buildSubmitSchema(dispatch.campaign_type, registries);
        const parsed = schema.safeParse(payload);
        if (parsed.success) return { success: true, errors: [] };
        const errors = parsed.error.issues.map((i) => {
            const path = i.path.join(".");
            return path ? `${path}: ${i.message}` : i.message;
        });
        return { success: false, errors };
    }, [payload, dispatch.campaign_type, registries]);

    const submitDisabled =
        submitting
        || dispatch.blocking_errors.length > 0
        || !zodResult.success
        || registriesLoading;

    // Phase 14.8 W4 — gate Step 2 → Step 3 advancement on out-of-bounds
    // input errors only (not missing-required-field errors — those
    // can only be filled in on Step 3 anyway). Issue codes
    // `too_big` / `too_small` / `invalid_enum_value` indicate the
    // operator typed a value that violates a constraint; `invalid_type`
    // with `received: "undefined"` is a not-yet-filled-in field that
    // shouldn't block step advancement.
    const outOfBoundsIssues = (!zodResult.success && zodResult.error?.issues)
        ? zodResult.error.issues.filter((i) =>
            i.code === "too_big"
            || i.code === "too_small"
            || i.code === "invalid_enum_value"
            || (i.code === "custom" && i.message?.match(/exceeds the.*-run cap/i)))
        : [];
    const nextDisabled =
        (currentStep === 1
            && (!formValues.scenario_id
                || !formValues.interceptor_airframe_id
                || !formValues.target_airframe_id))
        || (currentStep === 2
            && Boolean(dispatch.campaign_type)
            && outOfBoundsIssues.length > 0);
    const _step1MissingFields = (currentStep === 1)
        ? [
            !formValues.scenario_id && "Baseline scenario",
            !formValues.interceptor_airframe_id && "Interceptor airframe",
            !formValues.target_airframe_id && "Target airframe",
        ].filter(Boolean)
        : [];
    const nextDisabledReason =
        nextDisabled
            ? (currentStep === 1
                ? `Pick the following before continuing: ${_step1MissingFields.join(", ")}.`
                : (outOfBoundsIssues[0]?.message
                   || "Resolve the inline validation errors below to advance."))
            : null;

    const handleStepChange = (next) => {
        const safe = clampStep(next);
        const sp = new URLSearchParams(searchParams);
        sp.set("step", String(safe));
        setSearchParams(sp);
    };

    const onSubmit = async () => {
        if (submitDisabled) return;
        setSubmitting(true);
        setSubmitErr(null);
        try {
            let created;
            if (dispatch.dispatch_endpoint === GEOMETRY_DISPATCH_ENDPOINT) {
                created = await geometryCampaigns.create(payload);
            } else if (dispatch.dispatch_endpoint === OPTIMISATION_DISPATCH_ENDPOINT) {
                created = await optimisationCampaigns.create(payload);
            } else if (dispatch.dispatch_endpoint === PK_DISPATCH_ENDPOINT) {
                created = await pkCampaigns.create(payload);
            } else {
                throw new Error("No dispatch endpoint resolved.");
            }
            const newId = created?.id || created?.campaign_id;
            toast.success(`Campaign created — ${dispatch.campaign_type}`);
            if (newId) {
                let family = "pk";
                if (dispatch.dispatch_endpoint === GEOMETRY_DISPATCH_ENDPOINT) family = "geometry";
                else if (dispatch.dispatch_endpoint === OPTIMISATION_DISPATCH_ENDPOINT) family = "optimisation";
                navigate(`/campaigns/${newId}`, { state: { family } });
            } else {
                navigate("/campaigns");
            }
        } catch (e) {
            const detail = e?.response?.data?.detail || e?.message || "Submit failed.";
            setSubmitErr(detail);
            toast.error(`Submit failed: ${detail}`);
        } finally {
            setSubmitting(false);
        }
    };

    const renderRegistriesBanner = () => {
        if (registriesLoading) {
            return (
                <Alert
                    data-testid="registries-loading-banner"
                    className="border-ux-border-1 bg-ux-surface-2/40"
                >
                    <Loader2 className="size-4 animate-spin" />
                    <AlertDescription>Loading campaign registries…</AlertDescription>
                </Alert>
            );
        }
        if (registriesError) {
            return (
                <Alert variant="destructive" data-testid="registries-error-banner">
                    <AlertTriangle className="size-4" />
                    <AlertDescription>
                        Failed to load one or more campaign registries. Check your network or
                        ask an admin to verify the backend is reachable.
                    </AlertDescription>
                </Alert>
            );
        }
        return null;
    };

    return (
        <div className="space-y-6" data-testid="new-campaign-wizard" data-current-step={currentStep}>
            <PageHeader
                title="New campaign"
                subtitle="Guided builder. Pick a scenario, toggle the variation dimensions, and review the dispatch plan before submitting."
            />

            {/* Phase 18.B — legacy builder is deprecated. Bookmarks
                still land here, but new campaigns should use the
                unified builder. */}
            <Alert
                data-testid="legacy-builder-deprecation-banner"
                className="border-amber-500/40 bg-amber-500/10"
            >
                <AlertTriangle className="size-4 text-amber-300" />
                <AlertDescription className="text-amber-200">
                    <strong>This legacy builder is deprecated.</strong> New
                    campaigns should be authored in the{" "}
                    <a
                        href="/campaigns/new"
                        className="underline underline-offset-2 hover:text-amber-100"
                        data-testid="legacy-builder-unified-link"
                    >
                        unified builder
                    </a>
                    . Existing legacy runs remain visible read-only on the
                    Campaigns list.
                </AlertDescription>
            </Alert>

            {renderRegistriesBanner()}

            <WizardShell
                currentStep={currentStep}
                onStepChange={handleStepChange}
                onSubmit={onSubmit}
                submitDisabled={submitDisabled}
                nextDisabled={nextDisabled}
                nextDisabledReason={nextDisabledReason}
                submitLabel={
                    submitting
                        ? "Submitting…"
                        : dispatch.campaign_type
                            ? `Create campaign · lane=${dispatch.campaign_type}`
                            : "Create campaign"
                }
            >
                {currentStep === 1 && (
                    <div className="space-y-4" data-testid="wizard-step-1-content">
                        <div>
                            <Label className="mb-1 block text-xs font-medium uppercase tracking-wide text-ux-text-3">
                                Campaign name
                            </Label>
                            <Input
                                placeholder="e.g. Weak Baseline Demo — sensitivity sweep"
                                value={formValues.name || ""}
                                onChange={(e) => setValue("name", e.target.value)}
                                data-testid="wizard-name-input"
                            />
                        </div>
                        <div data-testid="wizard-scenario-picker-host">
                            {/* Phase 14.5 Step 6 (Y2c) — registry-backed
                                scenario picker, replacing the prior
                                free-form `<Input>` paste box. Reuses
                                the platform-wide BaselineScenarioPicker
                                so non-runnable scenarios (ROE
                                candidates / quick-sim / matrix-cell)
                                are filtered out at the picker level. */}
                            <BaselineScenarioPicker
                                value={formValues.scenario_id || ""}
                                onChange={(v) => setValue("scenario_id", v)}
                                data-testid="wizard-scenario-picker"
                            />
                        </div>
                        {/* Phase 14.9 W2 — interceptor + target airframe
                            pickers. Operator-facing fix for PM Issue 1
                            (campaign-builder UX for varied trajectories).
                            Both pickers default to the first verified
                            airframe of their role; both required to
                            advance to Step 2 (gated by `nextDisabled`
                            with role-specific missing-field messaging). */}
                        <div data-testid="wizard-interceptor-picker-host">
                            <BaselineAirframePicker
                                role="interceptor"
                                value={formValues.interceptor_airframe_id || ""}
                                onChange={(v) => setValue("interceptor_airframe_id", v)}
                            />
                        </div>
                        <div data-testid="wizard-target-picker-host">
                            <BaselineAirframePicker
                                role="target"
                                value={formValues.target_airframe_id || ""}
                                onChange={(v) => setValue("target_airframe_id", v)}
                            />
                        </div>
                    </div>
                )}

                {currentStep === 2 && (
                    <div className="space-y-3" data-testid="wizard-step-2-content">
                        <p className="text-xs text-ux-text-3">
                            Toggle the dimensions of variation to include. All form sections
                            are registry-driven; mutual-exclusion rules surface as blocking
                            errors on the Review step.
                        </p>
                        <PerturbationSection
                            enabled={formValues.perturbation?.enabled}
                            onToggleEnabled={(v) => setValue("perturbation.enabled", v)}
                            value={formValues.perturbation}
                            onChange={(v) => setValue("perturbation", v)}
                            registry={registries.pk}
                        />
                        <SensitivitySection
                            enabled={formValues.sensitivity?.enabled}
                            onToggleEnabled={(v) => setValue("sensitivity.enabled", v)}
                            value={formValues.sensitivity}
                            onChange={(v) => setValue("sensitivity", v)}
                            registry={registries.pk}
                        />
                        <TargetPkSection
                            enabled={formValues.target_pk?.enabled}
                            onToggleEnabled={(v) => setValue("target_pk.enabled", v)}
                            value={formValues.target_pk}
                            onChange={(v) => setValue("target_pk", v)}
                            registry={registries.pk}
                        />
                        <GeometrySection
                            enabled={formValues.geometry?.enabled}
                            onToggleEnabled={(v) => setValue("geometry.enabled", v)}
                            value={formValues.geometry}
                            onChange={(v) => setValue("geometry", v)}
                            registry={registries.geometry}
                        />
                        <DegradationSection
                            enabled={formValues.degradation?.enabled}
                            onToggleEnabled={(v) => setValue("degradation.enabled", v)}
                            value={formValues.degradation}
                            onChange={(v) => setValue("degradation", v)}
                            registry={registries.degradation}
                        />
                        <OptimisationSection
                            enabled={formValues.optimisation?.enabled}
                            onToggleEnabled={(v) => setValue("optimisation.enabled", v)}
                            value={formValues.optimisation}
                            onChange={(v) => setValue("optimisation", v)}
                        />
                    </div>
                )}

                {currentStep === 3 && (
                    <div className="space-y-4" data-testid="wizard-step-3-content">
                        <div className="rounded-md border border-ux-border-1 bg-ux-surface-2/40 p-4">
                            <p className="text-sm font-medium text-ux-text-2">Review &amp; Submit</p>
                            <p className="mt-1 text-xs text-ux-text-3">
                                Resolved dispatch lane:{" "}
                                <strong data-testid="dispatch-lane-readout">
                                    {dispatch.campaign_type || "(none — see errors below)"}
                                </strong>
                                {dispatch.dispatch_endpoint && (
                                    <>
                                        {" — endpoint "}
                                        <code className="rounded bg-ux-surface-1 px-1 py-0.5 text-[11px]">
                                            POST {dispatch.dispatch_endpoint}
                                        </code>
                                    </>
                                )}.
                            </p>
                        </div>

                        {dispatch.blocking_errors.length > 0 && (
                            <Alert variant="destructive" data-testid="dispatch-blocking-errors">
                                <AlertTriangle className="size-4" />
                                <AlertDescription>
                                    <p className="font-medium">Toggle conflicts (must resolve):</p>
                                    <ul className="mt-1 list-inside list-disc text-xs">
                                        {dispatch.blocking_errors.map((msg, i) => (
                                            <li key={i} data-testid={`dispatch-blocking-error-${i}`}>
                                                {msg}
                                            </li>
                                        ))}
                                    </ul>
                                </AlertDescription>
                            </Alert>
                        )}

                        {dispatch.campaign_type && !zodResult.success && (
                            <Alert variant="destructive" data-testid="zod-field-errors">
                                <AlertTriangle className="size-4" />
                                <AlertDescription>
                                    <p className="font-medium">Field-level validation errors:</p>
                                    <ul className="mt-1 list-inside list-disc text-xs">
                                        {zodResult.errors.map((msg, i) => (
                                            <li key={i} data-testid={`zod-field-error-${i}`}>
                                                {msg}
                                            </li>
                                        ))}
                                    </ul>
                                </AlertDescription>
                            </Alert>
                        )}

                        {submitErr && (
                            <Alert variant="destructive" data-testid="dispatch-submit-error">
                                <AlertTriangle className="size-4" />
                                <AlertDescription>
                                    Submit failed: {String(submitErr)}
                                </AlertDescription>
                            </Alert>
                        )}

                        {dispatch.campaign_type && zodResult.success && dispatch.blocking_errors.length === 0 && (
                            <details
                                className="text-xs text-ux-text-3"
                                data-testid="dispatch-payload-preview-host"
                            >
                                <summary className="cursor-pointer">Show assembled payload preview</summary>
                                <pre
                                    className="mt-2 overflow-auto rounded bg-ux-surface-1 p-3 text-[11px]"
                                    data-testid="dispatch-payload-preview"
                                >
                                    {JSON.stringify(payload, null, 2)}
                                </pre>
                            </details>
                        )}
                    </div>
                )}
            </WizardShell>
        </div>
    );
}
